/**
 * Shown when the signed-in user's last email to us bounced permanently or was
 * marked as spam.
 *
 * Why this exists: the SES event consumer records `email_status`, but recording
 * a bad address without a way to correct it converts a silent failure into a
 * visible dead end. This is the correction path.
 *
 * `email_status` reaches the frontend only because it is named in
 * get_profile's explicit allowlist. An absent or empty value means HEALTHY --
 * never treat a missing field as a problem.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/api';

export const EmailBounceBanner: React.FC = () => {
  const { t } = useTranslation('navigation');
  const { locale } = useParams<{ locale: string }>();
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'saving' | 'sent' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const emailStatus = user?.email_status;
  // Absent/'' is healthy. Only these two states are actionable.
  if (emailStatus !== 'bounced' && emailStatus !== 'complained') return null;
  if (status === 'sent') {
    return (
      <div role="status" style={styles.banner}>
        {t('emailBounce.sent', 'Check your new inbox for a verification link.')}
      </div>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('saving');
    setMessage('');
    try {
      await authService.changeEmail(newEmail.trim(), locale || 'en');
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setMessage(
        err instanceof Error
          ? err.message
          : t('emailBounce.error', 'Could not update your email address.')
      );
    }
  };

  return (
    <div role="alert" style={styles.banner}>
      <p style={styles.text}>
        {emailStatus === 'bounced'
          ? t('emailBounce.bounced', "We couldn't deliver email to {{email}}.", {
              email: user?.email,
            })
          : t('emailBounce.complained', 'Email to {{email}} was marked as spam.', {
              email: user?.email,
            })}
      </p>

      {!open ? (
        <button type="button" style={styles.button} onClick={() => setOpen(true)}>
          {t('emailBounce.update', 'Update email address')}
        </button>
      ) : (
        <form onSubmit={submit} style={styles.form}>
          <label htmlFor="bounce-new-email" style={styles.srOnly}>
            {t('emailBounce.newEmail', 'New email address')}
          </label>
          <input
            id="bounce-new-email"
            type="email"
            required
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder={t('emailBounce.newEmail', 'New email address')}
            style={styles.input}
          />
          <button type="submit" style={styles.button} disabled={status === 'saving'}>
            {status === 'saving'
              ? t('emailBounce.saving', 'Saving...')
              : t('emailBounce.save', 'Save')}
          </button>
        </form>
      )}

      {status === 'error' && <p style={styles.error}>{message}</p>}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  banner: {
    background: '#FFF4E5',
    border: '1px solid #F5A623',
    borderRadius: 8,
    padding: '12px 16px',
    margin: '12px 0',
    color: '#1a1a2e',
  },
  text: { margin: '0 0 8px 0', fontSize: 14 },
  form: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  input: {
    flex: '1 1 220px',
    padding: '8px 10px',
    borderRadius: 6,
    border: '1px solid #ccc',
    fontSize: 14,
  },
  button: {
    background: '#F5A623',
    color: '#1a1a2e',
    border: 'none',
    borderRadius: 50,
    padding: '8px 18px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  error: { margin: '8px 0 0 0', color: '#b00020', fontSize: 13 },
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
  },
};

export default EmailBounceBanner;
